import { extendObservable, computed } from 'mobx'
import get from 'lodash/get'
import omit from 'lodash/omit'

import { getAccountSuggestions, contractApi, employeeApi, parentAccountApi } from '@atheneum/api'

class ContractsSearchStore {
  constructor (staticData) {
    extendObservable(this, {
      loading: null,
      error: null,
      contractsCount: null,
      contracts: [],
      staticData,
      query: this.getEmptyQuery(),
      contractTypes: computed(() => {
        return staticData.contractTypes
      })
    })
  }

  resetFilter () {
    this.query = this.getEmptyQuery()
    this.filter()
  }

  getEmptyQuery () {
    // can't use staticData constants here because staticData store is not yet loaded when this function is called
    const ACTIVE = 2
    const EXPIRING_SOON = 3

    return {
      page: 1,
      fromDate: null,
      toDate: null,
      contractStatuses: [ACTIVE, EXPIRING_SOON],
      searchString: '',
      contractTypeId: 0,
      account: null,
      atheneumOfficeId: 0,
      parentAccount: null,
      employee: null,
      limit: 15
    }
  }

  filter = () => {
    this.loading = true

    const preparedQuery = {
      ...omit(this.query, ['parentAccount', 'account', 'employee']),
      parentAccountId: get(this.query.parentAccount, 'id', 0),
      accountId: get(this.query.account, 'id', 0),
      employeeId: get(this.query.employee, 'id', 0)
    }

    return contractApi.search(preparedQuery)
      .then(res => {
        this.contractsCount = res.count
        this.contracts.replace(res.rows)
      })
      .catch(err => {
        this.error = err
      })
      .then(() => {
        this.loading = false
      })
  }

  create = contractApi.create

  changePage = page => {
    this.query.page = page
    this.filter()
  }

  accountAutocomplete = value => {
    if (!value) return Promise.resolve([])

    return getAccountSuggestions(value)
  }

  parentAccountAutocomplete = value => {
    if (!value) return Promise.resolve([])

    return parentAccountApi.typeaheadSearch(value)
  }

  employeeAutocomplete = value => {
    const { EMPLOYEE_POSITIONS } = this.staticData.CONST
    if (!value) {
      return Promise.resolve([])
    }
    // Principals, Admins
    const roles = [
      EMPLOYEE_POSITIONS.BD,
      EMPLOYEE_POSITIONS.ACCOUNTING,
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.EXECUTIVE,
      EMPLOYEE_POSITIONS.ADMIN,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ]

    return employeeApi.typeaheadSearch(roles, value)
  }

  changeFromDate = date => {
    if (!date) return
    this.query.fromDate = date.toISOString()
    this.query.page = 1
    this.filter()
  }

  changeToDate = date => {
    if (!date) return
    this.query.toDate = date.toISOString()
    this.query.page = 1
    this.filter()
  }

  changeParentAccount = parentAccount => {
    this.query.parentAccount = parentAccount
    this.query.page = 1
    this.filter()
  }
  changeAccount = account => {
    this.query.account = account
    this.query.page = 1
    this.filter()
  }

  changeAtheneumContractParty = atheneumOfficeId => {
    this.query.atheneumOfficeId = atheneumOfficeId
    this.query.page = 1
    this.filter()
  }

  changeEmployee = employee => {
    this.query.employee = employee
    this.query.page = 1
    this.filter()
  }

  changeContractStatus = statuses => {
    this.query.contractStatuses = statuses
    this.query.page = 1
    this.filter()
  }

  changeContractType = type => {
    this.query.contractTypeId = type
    this.query.page = 1
    this.filter()
  }

  changeContractCode = searchString => {
    this.query.searchString = searchString
    this.query.page = 1
    this.filter()
  }
}

export default ContractsSearchStore
